import React, { useMemo } from 'react';
import { Modal } from 'antd';
import { Printer, X } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import './KsefInvoiceView.css';

const formatPLN = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '0,00';
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (str) => (str ? dayjs(str).format('DD.MM.YYYY') : '—');
const formatDateTime = (str) => (str ? dayjs(str).format('DD.MM.YYYY HH:mm') : '—');

const ROLE_LABEL = {
  '1': 'Sprzedawca',
  '2': 'Nabywca',
  '3': 'Dostawca / wystawca',
  '4': 'Faktor',
  '5': 'Odbiorca',
  '6': 'Płatnik',
  '7': 'Drugi nabywca',
  '8': 'Wystawca faktury',
  '9': 'Przedstawiciel podatkowy',
  '11': 'Dodatkowy podmiot (np. akronim)',
  '12': 'Wystawca w imieniu nabywcy',
  '13': 'Nabywca docelowy',
};

const NUM_TO_PL_WORDS = (() => {
  const ones = ['zero', 'jeden', 'dwa', 'trzy', 'cztery', 'pięć', 'sześć', 'siedem', 'osiem', 'dziewięć',
    'dziesięć', 'jedenaście', 'dwanaście', 'trzynaście', 'czternaście', 'piętnaście', 'szesnaście',
    'siedemnaście', 'osiemnaście', 'dziewiętnaście'];
  const tens = ['', '', 'dwadzieścia', 'trzydzieści', 'czterdzieści', 'pięćdziesiąt', 'sześćdziesiąt',
    'siedemdziesiąt', 'osiemdziesiąt', 'dziewięćdziesiąt'];
  const hundreds = ['', 'sto', 'dwieście', 'trzysta', 'czterysta', 'pięćset', 'sześćset', 'siedemset',
    'osiemset', 'dziewięćset'];
  const sub1k = (n) => {
    if (n === 0) return '';
    const h = Math.floor(n / 100);
    const t = Math.floor((n % 100) / 10);
    const o = n % 10;
    let out = [];
    if (h) out.push(hundreds[h]);
    if (t === 1) { out.push(ones[10 + o]); }
    else { if (t) out.push(tens[t]); if (o) out.push(ones[o]); }
    return out.join(' ');
  };
  const thouForm = (n) => (n === 1 ? 'tysiąc' : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'tysiące' : 'tysięcy'));
  return (val) => {
    if (val == null || isNaN(val)) return '';
    const num = Math.floor(Math.abs(val));
    if (num === 0) return 'zero';
    const t = Math.floor(num / 1000);
    const r = num % 1000;
    const out = [];
    if (t) out.push(`${sub1k(t)} ${thouForm(t)}`);
    if (r) out.push(sub1k(r));
    return out.join(' ').replace(/\s+/g, ' ').trim();
  };
})();

const grossInWords = (val, currency = 'PLN') => {
  if (val == null || isNaN(val)) return '';
  const intPart = Math.floor(val);
  const grosze = Math.round((val - intPart) * 100);
  const curMain = currency === 'PLN' ? 'złotych' : currency;
  return `${NUM_TO_PL_WORDS(intPart)} ${curMain} ${String(grosze).padStart(2, '0')}/100`;
};

const KsefInvoiceView = ({ invoice, open, onClose }) => {
  const vatBuckets = useMemo(() => {
    const items = invoice?.lineItems || [];
    const map = new Map();
    for (const li of items) {
      const rate = li.vatRate ?? 0;
      const key = String(rate);
      if (!map.has(key)) map.set(key, { rate, net: 0, vat: 0, gross: 0 });
      const b = map.get(key);
      b.net += parseFloat(li.netValue) || 0;
      b.vat += parseFloat(li.vatValue) || 0;
      b.gross += parseFloat(li.grossValue) || 0;
    }
    return [...map.values()].sort((a, b) => b.rate - a.rate);
  }, [invoice]);

  if (!invoice) return null;

  const totalNet = parseFloat(invoice.netAmount) || 0;
  const totalVat = parseFloat(invoice.vatAmount) || 0;
  const totalGross = parseFloat(invoice.grossAmount) || 0;
  const currency = invoice.currency || 'PLN';
  const isPaid = invoice.payment?.isPaid;
  const acronym = invoice.relatedEntities?.find(e => String(e.role) === '11')?.name;
  const portalInfo = invoice.portalInfo;

  const handlePrint = () => {
    document.body.classList.add('ksef-printing');
    setTimeout(() => {
      window.print();
      document.body.classList.remove('ksef-printing');
    }, 100);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1080}
      centered
      destroyOnClose
      closable={false}
      maskClosable
      className="ksef-doc-modal"
      styles={{
        body: { padding: 0 },
        content: { padding: 0, background: 'transparent', boxShadow: 'none' },
        mask: { backdropFilter: 'blur(2px)', background: 'rgba(28, 22, 10, 0.55)' },
      }}
    >
      <div className="ksef-doc-shell">
        <div className="ksef-doc-toolbar">
          <button className="ksef-doc-btn" onClick={onClose}>
            <X size={16} weight="bold" />
            <span>Zamknij</span>
          </button>
          <div className="ksef-doc-toolbar-spacer" />
          <div className="ksef-doc-toolbar-meta">
            <span className="ksef-doc-meta-label">W portalu</span>
            <span className={`ksef-doc-toolbar-pill ${invoice.inPortal ? 'is-yes' : 'is-no'}`}>
              {invoice.inPortal
                ? portalInfo?.driverLabel ? `Dodana · ${portalInfo.driverLabel}` : 'Dodana'
                : 'Brak'}
            </span>
          </div>
          <button className="ksef-doc-btn ksef-doc-btn--primary" onClick={handlePrint}>
            <Printer size={16} weight="bold" />
            <span>Drukuj</span>
          </button>
        </div>

        <article className="ksef-doc-paper">
          {invoice.payment != null && (
            <div className={`ksef-stamp ${isPaid ? 'ksef-stamp--paid' : 'ksef-stamp--due'}`}>
              <span className="ksef-stamp-line">{isPaid ? 'OPŁACONA' : 'NIEOPŁACONA'}</span>
              <span className="ksef-stamp-sub">
                {isPaid && invoice.payment.paymentDate
                  ? formatDate(invoice.payment.paymentDate)
                  : 'KSeF · FA(2)'}
              </span>
            </div>
          )}

          <header className="ksef-doc-head">
            <div className="ksef-doc-head-titles">
              <span className="ksef-doc-eyebrow">Krajowy System e-Faktur</span>
              <h1 className="ksef-doc-title">
                <span>Faktura</span>
                <em>{invoice.invoiceKind || 'VAT'}</em>
              </h1>
              <div className="ksef-doc-head-numbers">
                <div>
                  <span className="ksef-doc-mini-label">Nr faktury</span>
                  <strong className="ksef-doc-invoice-number">{invoice.invoiceNumber || '—'}</strong>
                </div>
                <div>
                  <span className="ksef-doc-mini-label">Data wystawienia</span>
                  <strong>{formatDate(invoice.issueDate)}</strong>
                </div>
                <div>
                  <span className="ksef-doc-mini-label">Data sprzedaży</span>
                  <strong>{formatDate(invoice.saleDate)}</strong>
                </div>
                {invoice.vehicleNumber && (
                  <div>
                    <span className="ksef-doc-mini-label">Nr pojazdu</span>
                    <strong className="ksef-doc-vehicle">{invoice.vehicleNumber}</strong>
                  </div>
                )}
                {acronym && (
                  <div>
                    <span className="ksef-doc-mini-label">Akronim</span>
                    <strong>{acronym}</strong>
                  </div>
                )}
              </div>
            </div>
            <aside className="ksef-doc-head-mark">
              <span className="ksef-doc-mark-label">KSeF</span>
              <code className="ksef-doc-mark-code">{invoice.ksefNumber || '—'}</code>
              <span className="ksef-doc-mark-sub">Pobrano · {formatDateTime(invoice.syncedAt)}</span>
            </aside>
          </header>

          {portalInfo && (
            <div className="ksef-doc-portal-banner">
              <span className="ksef-doc-portal-tag">W portalu</span>
              <div className="ksef-doc-portal-meta">
                <div>
                  <span className="ksef-doc-mini-label">Kierowca</span>
                  <strong>{portalInfo.driverLabel}</strong>
                </div>
                {portalInfo.addedAt && (
                  <div>
                    <span className="ksef-doc-mini-label">Dodano</span>
                    <strong>{formatDateTime(portalInfo.addedAt)}</strong>
                  </div>
                )}
                {portalInfo.source && (
                  <div>
                    <span className="ksef-doc-mini-label">Źródło</span>
                    <strong>{portalInfo.source}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          <section className="ksef-doc-parties">
            <div className="ksef-doc-party">
              <span className="ksef-doc-party-tag">Sprzedawca</span>
              <h3>{invoice.sellerName || '—'}</h3>
              <dl>
                <div><dt>NIP</dt><dd className="ksef-mono">{invoice.sellerNip || '—'}</dd></div>
                <div><dt>Adres</dt><dd>{[invoice.sellerAddress, invoice.sellerAddress2].filter(Boolean).join(', ') || '—'}</dd></div>
                {invoice.sellerCountry && <div><dt>Kraj</dt><dd>{invoice.sellerCountry}</dd></div>}
                {invoice.sellerPhone && <div><dt>Telefon</dt><dd className="ksef-mono">{invoice.sellerPhone}</dd></div>}
                {invoice.sellerEmail && <div><dt>E-mail</dt><dd>{invoice.sellerEmail}</dd></div>}
              </dl>
            </div>
            <div className="ksef-doc-party ksef-doc-party--buyer">
              <span className="ksef-doc-party-tag">Nabywca</span>
              <h3>{invoice.buyerName || '—'}</h3>
              <dl>
                <div><dt>NIP</dt><dd className="ksef-mono">{invoice.buyerNip || '—'}</dd></div>
                <div><dt>Adres</dt><dd>{[invoice.buyerAddress, invoice.buyerAddress2].filter(Boolean).join(', ') || '—'}</dd></div>
                {invoice.buyerCountry && <div><dt>Kraj</dt><dd>{invoice.buyerCountry}</dd></div>}
              </dl>
            </div>
          </section>

          {Array.isArray(invoice.lineItems) && invoice.lineItems.length > 0 && (
            <section className="ksef-doc-items">
              <header className="ksef-doc-section-head">
                <h4>Pozycje faktury</h4>
                <span className="ksef-doc-section-count">
                  {invoice.lineItems.length}{' '}
                  {invoice.lineItems.length === 1 ? 'pozycja' : invoice.lineItems.length < 5 ? 'pozycje' : 'pozycji'}
                </span>
              </header>
              <div className="ksef-doc-table-wrap">
                <table className="ksef-doc-table">
                  <thead>
                    <tr>
                      <th className="ksef-c-num">Lp</th>
                      <th className="ksef-c-desc">Nazwa towaru / usługi</th>
                      <th className="ksef-c-cn">CN · GTU</th>
                      <th className="ksef-c-qty">Ilość</th>
                      <th className="ksef-c-unit">J.m.</th>
                      <th className="ksef-c-money">Cena netto</th>
                      <th className="ksef-c-money">Wart. netto</th>
                      <th className="ksef-c-vat">VAT</th>
                      <th className="ksef-c-money">Kwota VAT</th>
                      <th className="ksef-c-money">Wart. brutto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems.map((li, idx) => (
                      <tr key={idx}>
                        <td className="ksef-c-num ksef-mono">{li.lineNumber || idx + 1}</td>
                        <td className="ksef-c-desc">{li.description || '—'}</td>
                        <td className="ksef-c-cn">
                          <span className="ksef-mono">{li.cnCode || '—'}</span>
                          {li.gtu ? <span className="ksef-c-gtu"> · {li.gtu}</span> : null}
                        </td>
                        <td className="ksef-c-qty ksef-mono">{formatPLN(li.quantity)}</td>
                        <td className="ksef-c-unit">{li.unit || '—'}</td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(li.unitPrice)}</td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(li.netValue)}</td>
                        <td className="ksef-c-vat ksef-mono">{li.vatRate}%</td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(li.vatValue)}</td>
                        <td className="ksef-c-money ksef-mono ksef-c-strong"><strong>{formatPLN(li.grossValue)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section className="ksef-doc-summary">
            <div className="ksef-doc-vat-summary">
              <header className="ksef-doc-section-head">
                <h4>Podsumowanie wg stawek VAT</h4>
              </header>
              <table className="ksef-doc-table ksef-doc-vat-table">
                <thead>
                  <tr>
                    <th className="ksef-c-vat-rate">Stawka</th>
                    <th className="ksef-c-money">Netto</th>
                    <th className="ksef-c-money">Kwota VAT</th>
                    <th className="ksef-c-money">Brutto</th>
                  </tr>
                </thead>
                <tbody>
                  {vatBuckets.length > 0 ? (
                    vatBuckets.map((b, i) => (
                      <tr key={i}>
                        <td className="ksef-c-vat-rate"><strong>{b.rate}%</strong></td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(b.net)}</td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(b.vat)}</td>
                        <td className="ksef-c-money ksef-mono">{formatPLN(b.gross)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="ksef-c-vat-rate"><strong>—</strong></td>
                      <td className="ksef-c-money ksef-mono">{formatPLN(totalNet)}</td>
                      <td className="ksef-c-money ksef-mono">{formatPLN(totalVat)}</td>
                      <td className="ksef-c-money ksef-mono">{formatPLN(totalGross)}</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="ksef-c-vat-rate"><strong>Razem</strong></td>
                    <td className="ksef-c-money ksef-mono"><strong>{formatPLN(totalNet)}</strong></td>
                    <td className="ksef-c-money ksef-mono"><strong>{formatPLN(totalVat)}</strong></td>
                    <td className="ksef-c-money ksef-mono"><strong>{formatPLN(totalGross)}</strong></td>
                  </tr>
                </tfoot>
              </table>
              <p className="ksef-doc-words">
                <span className="ksef-doc-mini-label">Słownie</span>
                <span>{grossInWords(totalGross, currency)}</span>
              </p>
            </div>

            <aside className="ksef-doc-total">
              <span className="ksef-doc-total-label">Razem do zapłaty</span>
              <span className="ksef-doc-total-value">
                <em className="ksef-mono">{formatPLN(totalGross)}</em>
                <small>{currency}</small>
              </span>
              <div className="ksef-doc-total-meta">
                {invoice.payment?.paymentDate && (
                  <span className="ksef-doc-total-due">
                    <span>Termin</span>
                    <strong>{invoice.payment.paymentDate}</strong>
                  </span>
                )}
                {invoice.payment?.method && (
                  <span className="ksef-doc-total-due">
                    <span>Forma</span>
                    <strong>{invoice.payment.method}</strong>
                  </span>
                )}
                {invoice.payment?.bankAccount && (
                  <span className="ksef-doc-total-due">
                    <span>Rachunek</span>
                    <strong className="ksef-mono">{invoice.payment.bankAccount}</strong>
                  </span>
                )}
              </div>
            </aside>
          </section>

          {Array.isArray(invoice.relatedEntities) && invoice.relatedEntities.length > 0 && (
            <section className="ksef-doc-related">
              <header className="ksef-doc-section-head">
                <h4>Podmioty powiązane</h4>
                <span className="ksef-doc-section-count">{invoice.relatedEntities.length}</span>
              </header>
              <div className="ksef-doc-related-grid">
                {invoice.relatedEntities.map((e, i) => (
                  <div key={i} className="ksef-doc-related-card">
                    <div className="ksef-doc-related-role">
                      <span>Rola {e.role}</span>
                      <em>{ROLE_LABEL[String(e.role)] || 'Podmiot'}</em>
                    </div>
                    <h5>{e.name || '—'}</h5>
                    <dl>
                      {e.nip && <div><dt>NIP</dt><dd className="ksef-mono">{e.nip}</dd></div>}
                      {e.pesel && <div><dt>PESEL</dt><dd className="ksef-mono">{e.pesel}</dd></div>}
                      {(e.address || e.address2) && (
                        <div><dt>Adres</dt><dd>{[e.address, e.address2].filter(Boolean).join(', ')}</dd></div>
                      )}
                      {e.country && <div><dt>Kraj</dt><dd>{e.country}</dd></div>}
                      {e.phone && <div><dt>Telefon</dt><dd className="ksef-mono">{e.phone}</dd></div>}
                      {e.email && <div><dt>E-mail</dt><dd>{e.email}</dd></div>}
                    </dl>
                  </div>
                ))}
              </div>
            </section>
          )}

          {invoice.additionalInfo && Object.keys(invoice.additionalInfo).length > 0 && (
            <section className="ksef-doc-extra">
              <header className="ksef-doc-section-head">
                <h4>Informacje dodatkowe</h4>
              </header>
              <dl className="ksef-doc-extra-grid">
                {Object.entries(invoice.additionalInfo).map(([k, v]) => (
                  <div key={k}>
                    <dt>{k.replace(/_/g, ' ').trim()}</dt>
                    <dd>{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          <footer className="ksef-doc-foot">
            <div>
              <span className="ksef-doc-mini-label">Rodzaj faktury</span>
              <strong>{invoice.invoiceKind || invoice.invoiceType || '—'}</strong>
            </div>
            <div>
              <span className="ksef-doc-mini-label">Samofakturowanie</span>
              <strong>{invoice.isSelfInvoicing ? 'Tak' : 'Nie'}</strong>
            </div>
            <div>
              <span className="ksef-doc-mini-label">Data utworzenia XML</span>
              <strong>{formatDateTime(invoice.creationDate)}</strong>
            </div>
            <div>
              <span className="ksef-doc-mini-label">System</span>
              <strong>{invoice.systemInfo || '—'}</strong>
            </div>
          </footer>
        </article>
      </div>
    </Modal>
  );
};

export default KsefInvoiceView;
